<template>
  <app-page title="Help" icon="question-circle" class="help-page">
    <template #custom-content>
      <div class="help-content">
        <div class="help-header">
          <h1>Help & Support</h1>
          <p class="help-subtitle">Get the assistance you need to make the most of your portal</p>
        </div>

        <div class="help-sections">
          <div class="help-section">
            <div class="help-section-icon">
              <font-awesome-icon :icon="['fas', 'headset']" />
            </div>
            <h3>Contact Support</h3>
            <p>Get in touch with our support team for personalized assistance.</p>
            <div class="support-actions">
              <button
                class="support-button primary"
                @click="openIntercomChat"
                title="Start a live chat with our support team"
              >
                <font-awesome-icon :icon="['fas', 'comments']" />
                Start Live Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import AppPage from '@/components/AppPage.vue';

// Intercom methods
const openIntercomChat = () => {
  if (window.Intercom) {
    window.Intercom('show');
  }
};
</script>

<style scoped>
.help-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.help-header {
  text-align: center;
  margin-bottom: 3rem;
}

.help-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--palette-color-brand-primary);
  margin-bottom: 1rem;
}

.help-subtitle {
  font-size: 1.2rem;
  color: var(--palette-color-default-lighten-20);
  margin: 0;
}

.help-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

.help-section {
  background: var(--palette-color-base-white);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.help-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
}

.help-section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: var(--palette-color-brand-primary-lighten-90);
  border-radius: 50%;
  margin-bottom: 1.5rem;
}

.help-section-icon svg {
  font-size: 1.5rem;
  color: var(--palette-color-brand-primary);
}

.help-section h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--palette-color-default);
  margin-bottom: 1rem;
}

.help-section p {
  color: var(--palette-color-default-lighten-20);
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
}

.support-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.support-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  min-width: 140px;
  justify-content: center;
}

.support-button.primary {
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
}

.support-button.primary:hover {
  background: var(--palette-color-brand-primary-darken-10);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.support-button.secondary {
  background: var(--palette-color-base-white);
  color: var(--palette-color-brand-primary);
  border: 2px solid var(--palette-color-brand-primary);
}

.support-button.secondary:hover {
  background: var(--palette-color-brand-primary-lighten-95);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.support-button svg {
  font-size: 1rem;
}

.help-footer {
  background: var(--palette-color-base-white);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.contact-info h4 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--palette-color-brand-primary);
  margin-bottom: 1rem;
}

.contact-info p {
  color: var(--palette-color-default-lighten-20);
  margin-bottom: 2rem;
}

.contact-methods {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.contact-method {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--palette-color-default);
  font-weight: 500;
}

.contact-method svg {
  color: var(--palette-color-brand-primary);
}

@media (max-width: 768px) {
  .help-content {
    padding: 1rem;
  }

  .help-header h1 {
    font-size: 2rem;
  }

  .help-sections {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .help-section {
    padding: 1.5rem;
  }

  .contact-methods {
    flex-direction: column;
    gap: 1rem;
  }

  .support-actions {
    flex-direction: column;
  }

  .support-button {
    width: 100%;
  }
}
</style>
